/**
 * React Hook for Firebase AI Logic
 *
 * Provides easy-to-use AI generation with loading states and error handling.
 */

import { useState, useCallback } from "react";
import {
  generateText,
  generateJSON,
  streamText,
  checkAIConfig,
  AI_MODELS,
  type AIModelId,
} from "@/lib/firebase-ai";

export interface UseFirebaseAIOptions {
  model?: AIModelId;
}

export interface UseFirebaseAIResult {
  // Generation functions
  generate: (prompt: string) => Promise<string>;
  generateJson: <T = unknown>(prompt: string) => Promise<T>;
  generateStream: (
    prompt: string,
    onChunk: (text: string) => void
  ) => Promise<string>;

  // State
  isLoading: boolean;
  error: string | null;
  lastResponse: string | null;

  // Config check
  checkConfig: () => Promise<{
    configured: boolean;
    location: string;
    error?: string;
  }>;

  // Clear state
  reset: () => void;
}

/**
 * Hook for using Firebase AI in React components
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { generate, isLoading, error } = useFirebaseAI();
 *
 *   const handleGenerate = async () => {
 *     const result = await generate("Explain Swiss VAT in one sentence");
 *     // Use result in your component
 *   };
 *
 *   return (
 *     <button onClick={handleGenerate} disabled={isLoading}>
 *       {isLoading ? "Generating..." : "Generate"}
 *     </button>
 *   );
 * }
 * ```
 */
export function useFirebaseAI(
  options: UseFirebaseAIOptions = {}
): UseFirebaseAIResult {
  const { model = AI_MODELS.GEMINI_2_5_FLASH } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResponse, setLastResponse] = useState<string | null>(null);

  const generate = useCallback(
    async (prompt: string): Promise<string> => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await generateText(prompt, model);
        setLastResponse(result);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [model]
  );

  const generateJson = useCallback(
    async <T = unknown>(prompt: string): Promise<T> => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await generateJSON<T>(prompt, model);
        setLastResponse(JSON.stringify(result, null, 2));
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [model]
  );

  const generateStream = useCallback(
    async (
      prompt: string,
      onChunk: (text: string) => void
    ): Promise<string> => {
      setIsLoading(true);
      setError(null);
      setLastResponse("");

      try {
        const result = await streamText(
          prompt,
          chunk => {
            setLastResponse(prev => (prev || "") + chunk);
            onChunk(chunk);
          },
          model
        );
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [model]
  );

  const reset = useCallback(() => {
    setError(null);
    setLastResponse(null);
    setIsLoading(false);
  }, []);

  return {
    generate,
    generateJson,
    generateStream,
    isLoading,
    error,
    lastResponse,
    checkConfig: checkAIConfig,
    reset,
  };
}

export { AI_MODELS };
export type { AIModelId };
